/**
 * Cliente HTTP del navegador contra la API Laravel (Sanctum SPA).
 *
 * Autenticación por cookie de sesión de primera parte:
 *  - credentials: "include" en cada petición (envía/recibe cookies).
 *  - Antes de cada mutación se asegura la cookie XSRF-TOKEN y se reenvía su
 *    valor en la cabecera X-XSRF-TOKEN (lo que valida el middleware CSRF).
 *
 * Para lectura de sesión en el servidor (SSR) usar src/lib/session.ts.
 */

const API_URL = process.env.NEXT_PUBLIC_API_URL ?? "https://apidev.legalsuite.io";

export type SessionUser = {
  id: number;
  name: string;
  email: string;
  avatar: string | null;
};

export type Workspace = { id: number; name: string; role: string };

export type Session = {
  user: SessionUser;
  abogado: { id: number; nombre: string | null } | null;
  role: string | null;
  workspaces: Workspace[];
};

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public payload: unknown = null,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

function readCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const escaped = name.replace(/([.*+?^${}()|[\]\\])/g, "\\$1");
  const match = document.cookie.match(
    new RegExp("(?:^|; )" + escaped + "=([^;]*)"),
  );
  return match ? decodeURIComponent(match[1]) : null;
}

/** Coloca la cookie XSRF-TOKEN si aún no existe (Sanctum). */
async function ensureCsrf(): Promise<void> {
  await fetch(`${API_URL}/sanctum/csrf-cookie`, { credentials: "include" });
}

export async function apiFetch<T = unknown>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const method = (options.method ?? "GET").toUpperCase();
  const mutating = !["GET", "HEAD", "OPTIONS"].includes(method);

  if (mutating && !readCookie("XSRF-TOKEN")) {
    await ensureCsrf();
  }

  const headers = new Headers(options.headers);
  headers.set("Accept", "application/json");
  if (options.body && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  if (mutating) {
    const xsrf = readCookie("XSRF-TOKEN");
    if (xsrf) headers.set("X-XSRF-TOKEN", xsrf);
  }

  const res = await fetch(`${API_URL}${path}`, {
    ...options,
    headers,
    credentials: "include",
  });

  if (!res.ok) {
    let payload: unknown = null;
    try {
      payload = await res.json();
    } catch {
      /* respuesta sin cuerpo JSON */
    }
    const message =
      (payload as { message?: string } | null)?.message ?? res.statusText;
    throw new ApiError(res.status, message, payload);
  }

  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const authApi = {
  login: (email: string, password: string, remember = false) =>
    apiFetch<Session>("/api/v1/login", {
      method: "POST",
      body: JSON.stringify({ email, password, remember }),
    }),
  logout: () => apiFetch("/api/v1/logout", { method: "POST" }),
  me: () => apiFetch<Session>("/api/v1/me"),
};
